import { MARQUE } from './marque';

/**
 * Repères visuels communs du CRM.
 *
 * Le CRM est écrit en styles en ligne, sans feuille de style : chaque page
 * redéfinissait ses couleurs, ses arrondis et ses espacements à la main, d'où
 * une apparence qui variait d'un écran à l'autre. Ce fichier rassemble ces
 * décisions en un seul endroit — on les change ici, elles changent partout.
 *
 * Principe retenu, repris des interfaces qui « respirent » : UNE couleur
 * d'accent (le marine du logo), tout le reste en gris. La couleur ne sert qu'à
 * désigner ce qui compte ; l'employer partout revient à ne rien désigner.
 */

// ── Couleurs ────────────────────────────────────────────────────────────────
export const C = {
  // Accent unique — celui du cabinet, défini dans marque.js. Le changer là-bas
  // rhabille l'application entière : menu, boutons, graphiques, jauges.
  marine: MARQUE.couleurAccent,
  marineClair: '#2d4a7a',
  marineSombre: '#132039',

  // Gris, du plus clair au plus foncé. Ils portent 90 % de l'interface.
  fond: '#f6f7f9',
  surface: '#ffffff',
  bordure: '#e8eaee',
  bordureForte: '#d8dce3',
  texteFaible: '#8b93a1',
  texteMoyen: '#5b6472',
  texte: '#1f2733',

  // Réservées au sens, jamais à la décoration : un état, pas un habillage.
  succes: '#0f9d6e',
  succesFond: '#e8f7f1',
  alerte: '#d97706',
  alerteFond: '#fef4e6',
  danger: '#dc2626',
  dangerFond: '#fdeced',
  info: '#2563eb',
  infoFond: '#eaf1fe',
};

/**
 * Couleurs d'accent des indicateurs.
 *
 * Chacune répond à une famille d'information, pas à une envie : le rouge
 * signale ce qui presse (une affaire chaude, une perte), le vert ce qui est
 * acquis, l'orange ce qui reste à encaisser, le bleu le suivi courant. Employé
 * ainsi, un coup d'œil suffit à repérer ce qui demande une action — ce qu'un
 * écran uniformément gris ne permet pas.
 *
 * Les fonds sont volontairement très pâles : la couleur porte sur le chiffre
 * et sur un liseré fin, jamais sur toute la carte.
 */
export const ACCENTS = {
  marine: { trait: C.marine, texte: C.marine, fond: '#eef1f6' },
  bleu: { trait: '#2563eb', texte: '#1d4ed8', fond: '#eaf1fe' },
  vert: { trait: '#0f9d6e', texte: '#047857', fond: '#e8f7f1' },
  rouge: { trait: '#dc2626', texte: '#b91c1c', fond: '#fdeced' },
  orange: { trait: '#d97706', texte: '#b45309', fond: '#fef4e6' },
  violet: { trait: '#7c3aed', texte: '#6d28d9', fond: '#f3eefe' },
  cyan: { trait: '#0891b2', texte: '#0e7490', fond: '#e6f6fa' },
};

// ── Espacements ─────────────────────────────────────────────────────────────
// Une échelle unique : les écarts arbitraires (13px ici, 17px là) sont ce qui
// donne l'impression de « pas fini ».
export const E = { xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '24px', xxl: '32px' };

// ── Typographie ─────────────────────────────────────────────────────────────
export const T = {
  police: "'Segoe UI', system-ui, -apple-system, 'Helvetica Neue', Arial, sans-serif",
  titre:      { fontSize: '20px', fontWeight: 700, color: C.texte, margin: 0, letterSpacing: '-0.01em' },
  sousTitre:  { fontSize: '13px', fontWeight: 400, color: C.texteFaible, margin: 0 },
  sectionMin: { fontSize: '11px', fontWeight: 700, color: C.texteFaible,
                textTransform: 'uppercase', letterSpacing: '0.07em' },
  // Intitulé d'un groupe d'indicateurs (Portefeuille, Crédit, Assurance).
  // Distinct de `sectionMin`, qui sert aussi aux groupes de la barre latérale :
  // les grossir ensemble alourdirait le menu, qui n'a rien demandé.
  groupe:     { fontSize: '16px', fontWeight: 700, color: C.texte,
                letterSpacing: '-0.01em' },
  etiquette:  { fontSize: '12px', fontWeight: 500, color: C.texteMoyen },
  chiffre:    { fontSize: '26px', fontWeight: 700, color: C.texte, lineHeight: 1.1,
                letterSpacing: '-0.02em' },
  corps:      { fontSize: '13px', color: C.texte },
};

export const RAYON = { sm: '6px', md: '10px', lg: '14px', rond: '999px' };

// Ombres discrètes : une bordure fine porte mieux la structure qu'une ombre
// marquée, qui salit vite un écran dense.
export const OMBRE = {
  carte: '0 1px 2px rgba(16, 24, 40, 0.04)',
  flottant: '0 8px 24px rgba(16, 24, 40, 0.12)',
};

// ── Briques réutilisables ───────────────────────────────────────────────────
export const carte = (extra = {}) => ({
  background: C.surface,
  border: `1px solid ${C.bordure}`,
  borderRadius: RAYON.lg,
  boxShadow: OMBRE.carte,
  ...extra,
});

export const enteteCarte = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: `${E.md} ${E.lg}`,
  borderBottom: `1px solid ${C.bordure}`,
};

export const bouton = (variante = 'principal') => {
  const base = {
    fontFamily: T.police, fontSize: '13px', fontWeight: 600,
    padding: '8px 14px', borderRadius: RAYON.sm, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: E.sm,
    transition: 'background 120ms ease, border-color 120ms ease',
    whiteSpace: 'nowrap',
  };
  const variantes = {
    principal: { ...base, background: C.marine, color: '#fff', border: `1px solid ${C.marine}` },
    secondaire: { ...base, background: C.surface, color: C.texte, border: `1px solid ${C.bordureForte}` },
    discret: { ...base, background: 'transparent', color: C.texteMoyen, border: '1px solid transparent' },
    danger: { ...base, background: C.surface, color: C.danger, border: `1px solid ${C.dangerFond}` },
  };
  return variantes[variante] || variantes.principal;
};

export const champ = {
  fontFamily: T.police, fontSize: '13px', color: C.texte,
  padding: '9px 12px', borderRadius: RAYON.sm,
  border: `1px solid ${C.bordureForte}`, background: C.surface,
  outline: 'none', width: '100%', boxSizing: 'border-box',
};

export const pastille = (ton = 'info') => {
  const tons = {
    succes: [C.succesFond, C.succes], alerte: [C.alerteFond, C.alerte],
    danger: [C.dangerFond, C.danger], info: [C.infoFond, C.info],
    neutre: [C.fond, C.texteMoyen],
  };
  const [fond, couleur] = tons[ton] || tons.info;
  return {
    display: 'inline-block', padding: '2px 9px', borderRadius: RAYON.rond,
    fontSize: '11px', fontWeight: 600, background: fond, color: couleur,
    whiteSpace: 'nowrap',
  };
};

// Mise en page en grille : `colonnes(4)` tient 4 cartes par ligne et retombe
// tout seul sur moins de colonnes quand la fenêtre rétrécit — sans media query
// à maintenir dans chaque page.
export const colonnes = (min = '220px', ecart = E.lg) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fit, minmax(${min}, 1fr))`,
  gap: ecart,
});
